import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Loader2, Minus, Plus, X } from "lucide-react";
import { toast } from "sonner";
import { useCart } from "@/context/CartContext";
import { api, formatApiError, money } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const inputCls =
  "w-full border border-white/15 bg-obsidian px-4 py-3 font-mono text-xs tracking-wider text-bone outline-none transition-colors placeholder:text-faint focus:border-vermilion";

export default function CartDrawer() {
  const { items, setQty, subtotal, open, setOpen } = useCart();
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", address: "", city: "", country: "" });

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const pay = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/checkout", {
        items: items.map((i) => ({ product_id: i.product.id, quantity: i.qty })),
        email: form.email,
        name: form.name,
        address: form.address,
        city: form.city,
        country: form.country,
        origin_url: window.location.origin,
      });
      toast.success(`Order ${data.order_code} reserved — completing payment`);
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(formatApiError(e));
      setLoading(false);
    }
  };

  return (
    <>
      <AnimatePresence>
        {open && (
          <>
            <motion.div
              className="fixed inset-0 z-[80] bg-black/60 backdrop-blur-sm"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setOpen(false)}
            />
            <motion.aside
              data-testid="cart-drawer"
              className="fixed right-0 top-0 z-[90] flex h-full w-full max-w-md flex-col border-l border-white/10 bg-onyx"
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ type: "spring", damping: 30, stiffness: 300 }}
            >
              <div className="flex items-center justify-between border-b border-white/10 px-6 py-5">
                <p className="font-mono text-xs uppercase tracking-[0.3em] text-smoke">Your Collection</p>
                <button onClick={() => setOpen(false)} data-testid="cart-close-button" aria-label="Close cart" className="text-smoke transition-colors hover:text-vermilion">
                  <X size={18} strokeWidth={1.5} />
                </button>
              </div>
              <div className="flex-1 overflow-y-auto px-6 py-4">
                {items.length === 0 && (
                  <p className="pt-16 text-center font-display text-2xl italic text-faint">Your reliquary is empty.</p>
                )}
                {items.map(({ product, qty }) => (
                  <div key={product.id} className="flex gap-4 border-b border-white/5 py-4" data-testid={`cart-item-${product.id}`}>
                    <img src={product.image} alt={product.name} className="h-20 w-16 border border-white/10 object-cover" />
                    <div className="flex flex-1 flex-col justify-between">
                      <div className="flex items-start justify-between gap-2">
                        <p className="font-display text-lg leading-tight text-bone">{product.name}</p>
                        <button onClick={() => setQty(product.id, 0)} data-testid={`cart-remove-${product.id}`} aria-label="Remove" className="text-faint transition-colors hover:text-vermilion">
                          <X size={14} />
                        </button>
                      </div>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center border border-white/15">
                          <button onClick={() => setQty(product.id, qty - 1)} data-testid={`cart-decrease-${product.id}`} className="px-2.5 py-1.5 text-smoke hover:text-bone" aria-label="Decrease">
                            <Minus size={12} />
                          </button>
                          <span className="w-8 text-center font-mono text-xs">{qty}</span>
                          <button onClick={() => setQty(product.id, qty + 1)} data-testid={`cart-increase-${product.id}`} className="px-2.5 py-1.5 text-smoke hover:text-bone" aria-label="Increase">
                            <Plus size={12} />
                          </button>
                        </div>
                        <span className="font-mono text-xs text-smoke">{money(product.price * qty)}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              {items.length > 0 && (
                <div className="border-t border-white/10 px-6 py-5">
                  <div className="mb-1 flex justify-between font-mono text-xs uppercase tracking-[0.25em] text-smoke">
                    <span>Subtotal</span>
                    <span data-testid="cart-subtotal" className="text-bone">{money(subtotal)}</span>
                  </div>
                  <p className="mb-4 font-mono text-[10px] tracking-wider text-faint">Shipping & duties calculated at checkout</p>
                  <button
                    onClick={() => setCheckoutOpen(true)}
                    data-testid="checkout-button"
                    className="w-full bg-vermilion py-4 font-mono text-xs uppercase tracking-[0.3em] text-obsidian transition-colors hover:bg-vermilion-dark"
                  >
                    Proceed to Checkout
                  </button>
                </div>
              )}
            </motion.aside>
          </>
        )}
      </AnimatePresence>

      <Dialog open={checkoutOpen} onOpenChange={setCheckoutOpen}>
        <DialogContent className="max-w-md border-white/10 bg-onyx text-bone" data-testid="checkout-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-3xl font-light">The Handover</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col gap-3 pt-2">
            <input required placeholder="FULL NAME" value={form.name} onChange={set("name")} data-testid="checkout-name-input" className={inputCls} />
            <input required type="email" placeholder="EMAIL" value={form.email} onChange={set("email")} data-testid="checkout-email-input" className={inputCls} />
            <input placeholder="STREET ADDRESS" value={form.address} onChange={set("address")} data-testid="checkout-address-input" className={inputCls} />
            <div className="grid grid-cols-2 gap-3">
              <input placeholder="CITY" value={form.city} onChange={set("city")} data-testid="checkout-city-input" className={inputCls} />
              <input placeholder="COUNTRY" value={form.country} onChange={set("country")} data-testid="checkout-country-input" className={inputCls} />
            </div>
            <div className="mt-2 flex justify-between border-t border-white/10 pt-4 font-mono text-xs uppercase tracking-[0.25em] text-smoke">
              <span>Total due</span>
              <span className="text-bone">{money(subtotal)}</span>
            </div>
            <button
              onClick={pay}
              disabled={loading || !form.name || !form.email}
              data-testid="stripe-pay-button"
              className="mt-2 flex items-center justify-center gap-2 bg-vermilion py-4 font-mono text-xs uppercase tracking-[0.3em] text-obsidian transition-colors hover:bg-vermilion-dark disabled:cursor-not-allowed disabled:opacity-40"
            >
              {loading ? <Loader2 className="animate-spin" size={16} /> : "Pay Securely with Stripe"}
            </button>
            <p className="text-center font-mono text-[10px] tracking-wider text-faint">You will be redirected to Stripe's secure checkout</p>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
